import React, { useEffect, useRef, useState } from 'react';
import { scheduleRoom, buildShareLinks } from '../../services/roomService';
import { generateRoomName } from '../../utils/helpers';
import {
  MAX_MEETING_DURATION_MINUTES,
  MAX_MEETING_DURATION_MS,
  formatDateTimeLocal,
} from '../../utils/meetingDuration';
import { useEmbeddedDialog } from '../../utils/embedDialog';
import ScheduledRoomLinkModal from './ScheduledRoomLinkModal';
import {
  DEFAULT_SAGE_LANGUAGE,
  SAGE_LANGUAGES,
} from '../../utils/sageLanguages';
import './ScheduleRoomModal.css';

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function isValidEmail(email) {
  const e = normalizeEmail(email);
  return Boolean(e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
}

export default function ScheduleRoomModal({
  isOpen,
  onClose,
  onSuccess,
  companyId = '',
  recruiterId = '',
  defaultHostName = '',
}) {
  const [showLinkModal, setShowLinkModal] = useState(false);
  useEmbeddedDialog(isOpen || showLinkModal);
  const [scheduledRoomData, setScheduledRoomData] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [hostName, setHostName] = useState(defaultHostName || '');
  const [scheduledStartTime, setScheduledStartTime] = useState('');
  const [scheduledEndTime, setScheduledEndTime] = useState('');
  const [participants, setParticipants] = useState([]);
  const [participantInput, setParticipantInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [sageJoin, setSageJoin] = useState(false);
  const [sageLanguage, setSageLanguage] = useState(DEFAULT_SAGE_LANGUAGE);
  const [languageOpen, setLanguageOpen] = useState(false);
  const languageRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    const endDefault = new Date(oneHourLater.getTime() + MAX_MEETING_DURATION_MS);
    setScheduledStartTime(formatDateTimeLocal(oneHourLater));
    setScheduledEndTime(formatDateTimeLocal(endDefault));
    if (defaultHostName) {
      setHostName((current) => current || defaultHostName);
    }
    setParticipants([]);
    setParticipantInput('');
    setError('');
    setInfo('');
    setLanguageOpen(false);
  }, [isOpen, defaultHostName]);

  useEffect(() => {
    if (!sageJoin) setLanguageOpen(false);
  }, [sageJoin]);

  useEffect(() => {
    if (!languageOpen) return undefined;
    const onPointerDown = (event) => {
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [languageOpen]);

  const handleStartChange = (value) => {
    setScheduledStartTime(value);
    setInfo('');
    if (!value) return;
    const start = new Date(value);
    if (Number.isNaN(start.getTime())) return;
    const nextEnd = new Date(start.getTime() + MAX_MEETING_DURATION_MS);
    setScheduledEndTime(formatDateTimeLocal(nextEnd));
  };

  const handleEndChange = (value) => {
    setScheduledEndTime(value);
    setInfo('');
  };

  const addParticipant = () => {
    const email = normalizeEmail(participantInput);
    if (!email) return;
    if (!isValidEmail(email)) {
      setError('Enter a valid participant email');
      return;
    }
    if (participants.includes(email)) {
      setError('That email is already added');
      return;
    }
    setError('');
    setParticipants((list) => [...list, email]);
    setParticipantInput('');
  };

  const removeParticipant = (email) => {
    setParticipants((list) => list.filter((item) => item !== email));
  };

  const handleParticipantKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addParticipant();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!title.trim() || !hostName.trim() || !scheduledStartTime || !scheduledEndTime) {
      setError('Please fill in all required fields');
      return;
    }

    let finalParticipants = participants;
    const pending = normalizeEmail(participantInput);
    if (pending) {
      if (!isValidEmail(pending)) {
        setError('Enter a valid participant email');
        return;
      }
      if (!finalParticipants.includes(pending)) {
        finalParticipants = [...finalParticipants, pending];
        setParticipants(finalParticipants);
      }
      setParticipantInput('');
    }

    const startTime = new Date(scheduledStartTime);
    const endTime = new Date(scheduledEndTime);
    const now = new Date();

    if (startTime <= now) {
      setError('Start time must be in the future');
      return;
    }

    if (endTime <= startTime) {
      setError('End time must be after start time');
      return;
    }

    setIsSubmitting(true);

    try {
      const roomName = generateRoomName();
      const result = await scheduleRoom({
        roomName,
        title: title.trim(),
        description: description.trim(),
        hostName: hostName.trim(),
        scheduledStartTime: startTime.toISOString(),
        scheduledEndTime: endTime.toISOString(),
        sageJoin,
        sageLanguage: sageJoin ? sageLanguage : undefined,
        companyId: companyId || undefined,
        recruiterId: recruiterId || undefined,
        participants: finalParticipants,
        syncCalendar: true,
      });

      const savedEnd = result.room?.scheduledEndTime || endTime.toISOString();
      const links = buildShareLinks(roomName);
      setScheduledRoomData({
        roomName,
        title: title.trim(),
        scheduledStartTime: startTime.toISOString(),
        scheduledEndTime: savedEnd,
        hostLink: links.hostLink,
        teamLink: links.teamLink,
        candidateLink: links.candidateLink,
        sageJoin: !!result.room?.sageJoin,
        participants: finalParticipants,
        inviteSummary: result.invites || null,
      });

      setTitle('');
      setDescription('');
      setHostName('');
      setScheduledStartTime('');
      setScheduledEndTime('');
      setParticipants([]);
      setParticipantInput('');
      setSageJoin(false);
      setSageLanguage(DEFAULT_SAGE_LANGUAGE);
      setLanguageOpen(false);
      setInfo('');
      onClose();
      setShowLinkModal(true);
      setIsSubmitting(false);
    } catch (err) {
      setError(err.message || 'Failed to schedule room');
      setIsSubmitting(false);
    }
  };

  const handleLinkModalClose = () => {
    const room = scheduledRoomData?.roomName;
    setShowLinkModal(false);
    setScheduledRoomData(null);
    if (onSuccess && room) {
      onSuccess(room);
    }
  };

  return (
    <>
      {isOpen && (
        <div className="mg-schedule-overlay" onClick={onClose}>
          <div className="mg-schedule" onClick={(e) => e.stopPropagation()}>
            <div className="mg-schedule-head">
              <h2>Schedule Meeting</h2>
              <button type="button" className="mg-schedule-close" onClick={onClose} aria-label="Close">
                ×
              </button>
            </div>

            <form className="mg-schedule-form" onSubmit={handleSubmit}>
              {error && <div className="mg-schedule-error">{error}</div>}

              <div className="mg-schedule-field">
                <label>
                  Meeting title <span>*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Candidate interview"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="mg-schedule-field">
                <label>
                  Host name <span>*</span>
                </label>
                <input
                  type="text"
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="mg-schedule-field">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional notes for this meeting"
                  disabled={isSubmitting}
                />
              </div>

              <div className="mg-schedule-field">
                <label>Participants</label>
                <div className="mg-schedule-participants">
                  {participants.length > 0 && (
                    <div className="mg-schedule-chips">
                      {participants.map((email) => (
                        <span key={email} className="mg-schedule-chip">
                          {email}
                          <button
                            type="button"
                            aria-label={`Remove ${email}`}
                            disabled={isSubmitting}
                            onClick={() => removeParticipant(email)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mg-schedule-participant-row">
                    <input
                      type="email"
                      value={participantInput}
                      onChange={(e) => {
                        setParticipantInput(e.target.value);
                        if (error) setError('');
                      }}
                      onKeyDown={handleParticipantKeyDown}
                      placeholder="Add participant email"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className="mg-schedule-add-participant"
                      onClick={addParticipant}
                      disabled={isSubmitting || !participantInput.trim()}
                    >
                      Add
                    </button>
                  </div>
                  <p className="mg-schedule-hint">
                    They get the same GetGeek invite email (date, time, meeting link).
                  </p>
                </div>
              </div>

              <div className="mg-schedule-field">
                <label>
                  Start <span>*</span>
                </label>
                <input
                  type="datetime-local"
                  value={scheduledStartTime}
                  onChange={(e) => handleStartChange(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="mg-schedule-field">
                <label>
                  End <span>*</span>
                  <small>default {MAX_MEETING_DURATION_MINUTES} min from start</small>
                </label>
                <input
                  type="datetime-local"
                  value={scheduledEndTime}
                  onChange={(e) => handleEndChange(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {info && <div className="mg-schedule-info">{info}</div>}

              <label className="mg-schedule-check">
                <input
                  type="checkbox"
                  checked={sageJoin}
                  onChange={(e) => setSageJoin(e.target.checked)}
                  disabled={isSubmitting}
                />
                Invite Sage when a host joins
              </label>

              {sageJoin && (
                <div className="mg-schedule-field" ref={languageRef}>
                  <label id="schedule-sage-language-label">Sage language</label>
                  <button
                    type="button"
                    className="mg-schedule-select"
                    aria-haspopup="listbox"
                    aria-expanded={languageOpen}
                    aria-labelledby="schedule-sage-language-label"
                    disabled={isSubmitting}
                    onClick={() => setLanguageOpen((open) => !open)}
                  >
                    <span>{sageLanguage}</span>
                    <span aria-hidden="true">▾</span>
                  </button>
                  {languageOpen && (
                    <div className="mg-schedule-select-menu" role="listbox">
                      {SAGE_LANGUAGES.map((lang) => (
                        <button
                          key={lang}
                          type="button"
                          role="option"
                          aria-selected={lang === sageLanguage}
                          className={lang === sageLanguage ? 'is-selected' : undefined}
                          onClick={() => {
                            setSageLanguage(lang);
                            setLanguageOpen(false);
                          }}
                        >
                          {lang}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button type="submit" className="mg-schedule-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Scheduling…' : 'Schedule Meeting'}
              </button>
            </form>
          </div>
        </div>
      )}

      {scheduledRoomData && (
        <ScheduledRoomLinkModal
          isOpen={showLinkModal}
          onClose={handleLinkModalClose}
          roomName={scheduledRoomData.roomName}
          title={scheduledRoomData.title}
          scheduledStartTime={scheduledRoomData.scheduledStartTime}
          scheduledEndTime={scheduledRoomData.scheduledEndTime}
          hostLink={scheduledRoomData.hostLink}
          teamLink={scheduledRoomData.teamLink}
          candidateLink={scheduledRoomData.candidateLink}
          sageJoin={scheduledRoomData.sageJoin}
          participants={scheduledRoomData.participants}
          inviteSummary={scheduledRoomData.inviteSummary}
        />
      )}
    </>
  );
}
